import { Box, Button, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { useParams } from "react-router-dom";
import PageLayout from "../components/common/page-layout";
import {
  SchemaRunType,
  useCreateSchemaTestSnapshotMutation,
  useSchemaRunQuery,
} from "../generated/graphql";
import { RequireAuth } from "../hooks/use-auth";

const styles = {
  results: {
    color: "black",
    display: "flex",
    flexDirection: "column",
    fontSize: "24px",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  snapshotAction: { m: 5 },
} satisfies Record<string, SxProps<Theme>>;

/** One schema run's matched anchors and extracted fields. */
export const SchemaRunPage = () => {
  const { uuid } = useParams();
  const { data, loading, refetch } = useSchemaRunQuery({
    variables: {
      uuid: uuid ?? "",
    },
  });
  const [
    createSchemaTestSnapshot,
    { loading: createSchemaTestSnapshotLoading },
  ] = useCreateSchemaTestSnapshotMutation();

  const split = data?.schemaRun?.key.split("/") ?? [];
  const filename = split.at(-1);

  const handleCreateSchemaTestSnapshot = async () => {
    await createSchemaTestSnapshot({
      variables: {
        createSchemaTestSnapshotInput: {
          uuid: uuid ?? "",
        },
      },
    });
    await refetch();
  };

  const content = (() => {
    if (loading) {
      return <Typography>Loading...</Typography>;
    }
    if (isNil(data)) {
      return <Typography>No data</Typography>;
    }
    return (
      <Box sx={styles.results}>
        <h3>Schema: {data.schemaRun?.schema?.name}</h3>
        <h3>File: {filename}</h3>
        <br />
        <h2>Fingerprint anchors matched</h2>
        <br />
        <table>
          <tr>
            <th>text</th>
            <th>substring</th>
          </tr>
          {data.schemaRun?.fingerprintAnchorMatches.map(
            (fingerprintAnchorMatch) => {
              return (
                <tr key={fingerprintAnchorMatch.uuid}>
                  <td>{fingerprintAnchorMatch.text}</td>
                  <td>{fingerprintAnchorMatch.substring}</td>
                </tr>
              );
            },
          )}
        </table>
        <br />
        <h2>Transform anchors matched</h2>
        <br />
        <table>
          <tr>
            <th>text</th>
          </tr>
          {data.schemaRun?.transformAnchorMatches.map(
            (transformAnchorMatch) => {
              return (
                <tr key={transformAnchorMatch.uuid}>
                  <td>{transformAnchorMatch.text}</td>
                </tr>
              );
            },
          )}
        </table>
        <br />
        <h2>Fields extracted</h2>
        <br />
        <table>
          <tr>
            <th>field</th>
            <th>value</th>
          </tr>
          {data.schemaRun?.extractedFields
            .sort((a, b) => {
              return a.field.localeCompare(b.field);
            })
            .map((extractedField) => {
              return (
                <tr key={extractedField.uuid}>
                  <td>{extractedField.field}</td>
                  <td>{extractedField.value}</td>
                </tr>
              );
            })}
        </table>
        <Box sx={styles.snapshotAction}>
          {data.schemaRun?.type === SchemaRunType.ManualRun && (
            <Button
              disabled={createSchemaTestSnapshotLoading}
              variant="contained"
              onClick={handleCreateSchemaTestSnapshot}
            >
              Create test from run
            </Button>
          )}
        </Box>
      </Box>
    );
  })();

  return (
    <RequireAuth>
      <PageLayout
        maxWidth={false}
        subtitle="Legacy schema-matching pipeline"
        title="Schema run"
      >
        {content}
      </PageLayout>
    </RequireAuth>
  );
};
